import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from '../../../application/stores/toastStore';
import { Button } from '../../components/Button';
import { errorMessage, useT } from '../../i18n/i18n';
import styles from './AuthLayout.module.css';

const RESEND_COOLDOWN_S = 60;

/** "Check your email" step shown after a sign-in link was sent. */
export function CheckEmail({
  email,
  resend,
  back,
}: {
  email: string;
  resend: () => Promise<void>;
  back: () => void;
}) {
  const t = useT();
  const [wait, setWait] = useState(RESEND_COOLDOWN_S);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  const onResend = async () => {
    setBusy(true);
    try {
      await resend();
      toast.success(t('auth.linkResent'));
      setWait(RESEND_COOLDOWN_S);
    } catch (err) {
      toast.error(errorMessage(t, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className={styles.emailSent}>
        {t('auth.checkEmailText')} <strong>{email}</strong>.
      </p>
      <div className={styles.notice} role="note">
        <Info size={18} aria-hidden="true" />
        <div>
          <strong>{t('auth.noLink')}</strong>{' '}
          <button
            type="button"
            className={styles.linkButton}
            onClick={onResend}
            disabled={busy || wait > 0}
          >
            {wait > 0 ? t('auth.resendIn', { n: wait }) : t('auth.resend')}
          </button>
          {t('auth.noLinkHint')}
        </div>
      </div>
      <Button variant="secondary" block onClick={back}>
        {t('auth.changeEmail')}
      </Button>
    </>
  );
}
